import {Dispatch, SetStateAction} from "react";
import AuthService from "../../api/authService";
import UserService, {IUpdateUserRequest} from "../../api/userService";
import {IRegisterUserBody} from "./Helper";
import {toastError, toastSuccess} from "../../component/shared/toast/Toast";
import i18n from "../../config/i18n";
import {SocialLoginType} from "../../util/constants";
import {getSafeBackendErrorMessage} from "../../util/utils";
import {createAsyncThunk} from "@reduxjs/toolkit";
import {SliceNames} from "../constants";
import {getWishlist} from "../wishlist/ActionCreators";
import {getClient} from "../client/ActionCreators";
import {subscribeUserToPushNotifications, unsubscribeUserFromPushNotifications} from "../../serviceWorkerRegistration";
import {RootState} from "../index";


export const logInUser =
    createAsyncThunk(`${SliceNames.AUTH}/logInUser`,
        async (logInRequest: { email: string, password: string }, thunkAPI) => {
            try {
                const res = await AuthService.login(logInRequest.email, logInRequest.password)
                const userDetails = await thunkAPI
                    .dispatch(getUserDetails(res.data))
                    .unwrap();
                if (!userDetails.is_admin && !userDetails.client_id) {
                    subscribeUserToPushNotifications().catch(err => {
                        console.error("Failed to subscribe to push notifications", err);
                    });
                }
            } catch (e: any) {
                return thunkAPI.rejectWithValue(getSafeBackendErrorMessage(e.response?.data?.detail))
            }
        })

export const socialLoginUser =
    createAsyncThunk(`${SliceNames.AUTH}/socialLoginUser`,
        async (logInRequest: { token: string, type: SocialLoginType }, thunkAPI) => {
            try {
                const res = await AuthService.socialLogin(logInRequest.token, logInRequest.type)
                const userDetails = await thunkAPI
                    .dispatch(getUserDetails(res.data))
                    .unwrap();
                if (!userDetails.is_admin && !userDetails.client_id) {
                    subscribeUserToPushNotifications().catch(err => {
                        console.error("Failed to subscribe to push notifications", err);
                    });
                }
            } catch (e: any) {
                return thunkAPI.rejectWithValue(getSafeBackendErrorMessage(e.response?.data?.detail))
            }
        })

export const getUserDetails =
    createAsyncThunk(`${SliceNames.AUTH}/getUserDetails`,
    async (logInResponse: { access_token: string, token_type: string }, thunkAPI) => {
        try {
            const res = await UserService.fetchLoggedInUserDetails(logInResponse.access_token)
            if (!res.data.is_admin) {
                thunkAPI.dispatch(getWishlist())
            }
            if (res.data.client_id) {
                thunkAPI.dispatch(getClient(res.data.client_id))
            }
            return {...logInResponse, ...res.data}
        } catch (e: any) {
            thunkAPI.dispatch(logOut(false))
            return thunkAPI.rejectWithValue(getSafeBackendErrorMessage(e.response?.data?.detail))
        }
    })

export const registerUser =
    createAsyncThunk(`${SliceNames.AUTH}/registerUser`,
        async (registerRequest: {
            body: IRegisterUserBody,
            interval: NodeJS.Timeout,
            setCounter: Dispatch<SetStateAction<number>>,
            scrollToTop: () => void
        }, thunkAPI) => {
            try {
                const res = await AuthService.register(registerRequest.body)
                registerRequest.interval = setInterval(() => {
                    registerRequest.setCounter((oldCounter) => {
                        if (oldCounter === 1) clearInterval(registerRequest.interval)
                        return oldCounter - 1
                    })
                }, 1000)
                registerRequest.scrollToTop()
                return res.data?.message ?? i18n.t('confirmation_mail_message')
            } catch (e: any) {
                return thunkAPI.rejectWithValue(getSafeBackendErrorMessage(e.response?.data?.detail))
            }
        })

export const updateUser =
    createAsyncThunk(`${SliceNames.AUTH}/updateUser`,
        async (request: {
            body: IUpdateUserRequest,
            image: File | undefined,
            handleDialogClose: () => void
        }, thunkAPI) => {
            try {
                const res = await UserService.updateUserDetails(request.body, request.image)
                toastSuccess(i18n.t('success_update_user'))
                request.handleDialogClose()
                return res.data
            } catch (e: any) {
                const errorMessage = getSafeBackendErrorMessage(
                    e.response?.data?.detail,
                    i18n.t('error_update_user')
                )
                toastError(errorMessage)
                return thunkAPI.rejectWithValue(errorMessage)
            }
        })


export const changeDefaultLanguage =
    createAsyncThunk(`${SliceNames.AUTH}/changeDefaultLanguage`,
        async (locale: string, thunkAPI) => {
            try {
                await UserService.updateLocale(locale)
                return locale
            } catch (e) {
                // toastError(i18n.t('error_change_language'))
                return thunkAPI.rejectWithValue(e)
            }
        })

export const logOut =
    createAsyncThunk(`${SliceNames.AUTH}/logOut`,
        async (withToast: boolean = false, thunkAPI) => {
            try {
                const userId = (thunkAPI.getState() as RootState).authReducer.loggedInUser.id
                const isAdmin = (thunkAPI.getState() as RootState).authReducer.loggedInUser.is_admin
                const isClient = !!(thunkAPI.getState() as RootState).authReducer.loggedInUser.client_id
                if (!isAdmin && !isClient) {
                    await unsubscribeUserFromPushNotifications(userId)
                }
                return withToast
            } catch (e) {
                console.error("Unsubscribe failed:", e)
                return withToast
            }
        })
